// src/components/vocabulary/admin/BatchManagement.tsx
import React, { useState, useEffect } from "react";
import { FaPlay, FaStop, FaEdit, FaUsers, FaCalendar } from "react-icons/fa";

interface CourseBatch {
  id?: number;
  batchId?: number;
  name?: string;
  batchName?: string;
  topic?: string;
  levels?: string[];
  mentorName?: string;
  startDate?: string;
  startingDate?: string;
  isActive?: boolean;
  students?: Array<{ firstName: string; lastName: string; email: string }>;
  totalStudents?: number;
  vocabularyEnabled: boolean;
  vocabularyStartDate: string | null;
  currentDayNumber: number;
  wordsPerDay: number;
}

const BatchManagement: React.FC = () => {
  const API = process.env.REACT_APP_API_BASE_URL;
  const [batches, setBatches] = useState<CourseBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<CourseBatch | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [vocabConfig, setVocabConfig] = useState({
    startDate: new Date().toISOString().split("T")[0],
    wordsPerDay: 5,
    levels: [] as string[],
  });

  useEffect(() => {
    loadBatches();
  }, []);

  const getBatchId = (batch: CourseBatch): number | undefined => {
    return batch.batchId || batch.id;
  };

  const loadBatches = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/api/admin/vocabulary/batches/with-course-batches`,
        { credentials: "include" },
      );
      if (res.ok) {
        const data: CourseBatch[] = await res.json();
        console.log("📊 Loaded batches with vocabulary status:", data);
        setBatches(data);
      }
    } catch (error) {
      console.error("Failed to load batches:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnableVocabulary = (batch: CourseBatch) => {
    setSelectedBatch(batch);
    setVocabConfig({
      startDate:
        batch.vocabularyStartDate || new Date().toISOString().split("T")[0],
      wordsPerDay: batch.wordsPerDay || 5,
      levels: batch.levels || [],
    });
    setShowConfigModal(true);
  };

  const handleLevelToggle = (level: string) => {
    setVocabConfig((prev) => ({
      ...prev,
      levels: prev.levels.includes(level)
        ? prev.levels.filter((l) => l !== level)
        : [...prev.levels, level],
    }));
  };

  // ✅ UPDATED: Handles start date change via new PATCH endpoint
  const handleSaveVocabularyConfig = async () => {
    if (!selectedBatch) return;
    if (vocabConfig.levels.length === 0) return;

    try {
      const batchName =
        selectedBatch.name || selectedBatch.batchName || "Unnamed Batch";
      const courseBatchId = selectedBatch.batchId || selectedBatch.id;

      // Step 1: Save levels and wordsPerDay via existing enable endpoint
      const payload = {
        courseBatchId,
        batchName,
        levels: vocabConfig.levels,
        startDate: vocabConfig.startDate,
        wordsPerDay: vocabConfig.wordsPerDay,
        isActive: true,
      };

      console.log("Saving vocabulary config:", payload);

      const res = await fetch(`${API}/api/admin/vocabulary/batches/enable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error("Failed to save:", errorText);
        alert("Failed to save configuration. Please try again.");
        return;
      }

      // Step 2: If vocabulary was already enabled and start date changed, call PATCH
      const startDateChanged =
        selectedBatch.vocabularyEnabled &&
        vocabConfig.startDate !== selectedBatch.vocabularyStartDate;

      if (startDateChanged) {
        // Fetch vocab batch ID via course batch ID
        const batchRes = await fetch(
          `${API}/api/admin/vocabulary/batches/by-course-batch/${courseBatchId}`,
          { credentials: "include" },
        );

        if (batchRes.ok) {
          const vocabBatch = await batchRes.json();
          const patchRes = await fetch(
            `${API}/api/admin/vocabulary/batches/${vocabBatch.batchId}/start-date`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ startDate: vocabConfig.startDate }),
            },
          );

          if (!patchRes.ok) {
            console.error("Failed to update start date");
            alert(
              "Configuration saved but start date update failed. Please try again.",
            );
            return;
          }
        }
      }

      setShowConfigModal(false);
      setSelectedBatch(null);

      const message = selectedBatch.vocabularyEnabled
        ? "✅ Configuration updated! Student progress preserved."
        : "✅ Vocabulary enabled successfully!";
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(null), 3000);
      loadBatches();
    } catch (error) {
      console.error("Error saving vocabulary:", error);
      alert("Error saving configuration. Please try again.");
    }
  };

  const handleDisableVocabulary = async (batch: CourseBatch) => {
    const batchName = batch.name || batch.batchName || "this batch";

    const confirmed = window.confirm(
      `Are you sure you want to disable vocabulary for "${batchName}"?\n\nStudents will no longer see vocabulary words, but their progress will be preserved.`,
    );

    if (!confirmed) return;

    try {
      const batchId = batch.batchId || batch.id;
      const res = await fetch(
        `${API}/api/admin/vocabulary/batches/disable/${batchId}`,
        {
          method: "POST",
          credentials: "include",
        },
      );

      if (res.ok) {
        setSuccessMessage("Vocabulary disabled successfully!");
        setTimeout(() => setSuccessMessage(null), 3000);
        loadBatches();
      } else {
        console.error("Failed to disable vocabulary");
      }
    } catch (error) {
      console.error("Error disabling vocabulary:", error);
    }
  };

  const getLevelColor = (level: string) => {
    const colors: { [key: string]: string } = {
      A1: "bg-green-100 text-green-700 border-green-300",
      A2: "bg-blue-100 text-blue-700 border-blue-300",
      B1: "bg-orange-100 text-orange-700 border-orange-300",
      B2: "bg-purple-100 text-purple-700 border-purple-300",
    };
    return colors[level] || "bg-gray-100 text-gray-700 border-gray-300";
  };

  return (
    <div>
      {/* Success Message Banner */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in">
          <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Vocabulary Batch Management
        </h2>
        <p className="text-gray-600">
          Enable vocabulary learning for your existing course batches
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4 mb-6">
        <p className="text-sm text-gray-700 mb-2">
          <strong>How it works:</strong> Click "Start Vocabulary" on any batch
          to enable daily vocabulary learning. You can select multiple levels
          (A1, A2, B1, B2) for each batch.
        </p>
        <p className="text-sm text-amber-700 font-medium">
          ⚠️ <strong>Important:</strong> Students without a vocabulary-enabled
          batch won't see any words. Only students in vocabulary-enabled batches
          will receive their daily words.
        </p>
      </div>

      {/* Batches Grid */}
      {loading ? (
        <div className="text-center py-10">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#009fe3]"></div>
          <p className="mt-4 text-gray-600">Loading batches...</p>
        </div>
      ) : batches.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 rounded-xl">
          <p className="text-xl text-gray-600 mb-2">No batches found</p>
          <p className="text-sm text-gray-500">
            Create batches in the Batch Management section first.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {batches.map((batch, index) => (
            <div
              key={getBatchId(batch) || `batch-${index}`}
              className="bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-2">
                      {batch.name || batch.batchName || "Unnamed Batch"}
                    </h3>
                    {batch.topic && (
                      <p className="text-sm text-gray-600 mb-2">
                        📚 {batch.topic}
                      </p>
                    )}
                    {batch.mentorName && (
                      <p className="text-xs text-gray-500 mb-2">
                        👨‍🏫 Mentor: {batch.mentorName}
                      </p>
                    )}
                    {batch.levels && batch.levels.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {batch.levels.map((level) => (
                          <span
                            key={level}
                            className={`px-2 py-1 rounded-full text-xs font-semibold border ${getLevelColor(level)}`}
                          >
                            {level}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-300">
                        ⚠️ Levels Required
                      </span>
                    )}
                  </div>

                  {batch.vocabularyEnabled ? (
                    <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      Active
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">
                      Inactive
                    </span>
                  )}
                </div>

                <div className="space-y-2 text-sm mb-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <FaUsers className="text-gray-400" />
                    <span>
                      <strong>{batch.totalStudents || 0}</strong> Students
                    </span>
                  </div>

                  {batch.vocabularyEnabled && (
                    <>
                      <div className="flex items-center gap-2 text-gray-600">
                        <FaCalendar className="text-gray-400" />
                        <span>
                          Started:{" "}
                          {new Date(
                            batch.vocabularyStartDate!,
                          ).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>Current Day:</span>
                        <span className="font-semibold text-gray-800">
                          {batch.currentDayNumber}
                        </span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>Words/Day:</span>
                        <span className="font-semibold text-gray-800">
                          {batch.wordsPerDay}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {batch.vocabularyEnabled ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEnableVocabulary(batch)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-semibold"
                    >
                      <FaEdit size={14} /> Configure
                    </button>
                    <button
                      onClick={() => handleDisableVocabulary(batch)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-semibold"
                    >
                      <FaStop size={14} /> Stop
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleEnableVocabulary(batch)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
                  >
                    <FaPlay size={14} /> Start Vocabulary
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && selectedBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-xl font-bold text-gray-800">
                {selectedBatch.vocabularyEnabled ? "📝 Update" : "🚀 Configure"}{" "}
                Vocabulary for{" "}
                {selectedBatch.name || selectedBatch.batchName || "Batch"}
              </h3>
              {selectedBatch.vocabularyEnabled && (
                <p className="text-sm text-gray-600 mt-1">
                  Currently on Day {selectedBatch.currentDayNumber} •{" "}
                  {selectedBatch.levels?.join(", ") || "No levels"} •{" "}
                  {selectedBatch.wordsPerDay} words/day
                </p>
              )}
            </div>

            <div className="p-6 space-y-4">
              {/* Batch Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                  {selectedBatch.topic && (
                    <div>
                      <strong>Topic:</strong> {selectedBatch.topic}
                    </div>
                  )}
                  <div>
                    <strong>Students:</strong>{" "}
                    {selectedBatch.totalStudents || 0}
                  </div>
                  {selectedBatch.mentorName && (
                    <div className="col-span-2">
                      <strong>Mentor:</strong> {selectedBatch.mentorName}
                    </div>
                  )}
                </div>
              </div>

              {/* Update vs Create Banner */}
              {selectedBatch.vocabularyEnabled ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg
                      className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-green-800 mb-1">
                        Updating Existing Configuration
                      </p>
                      <ul className="text-xs text-green-700 space-y-1">
                        <li>✅ Student progress & learned words preserved</li>
                        <li>
                          ✅ Start date is now editable — changing it shifts the
                          day counter
                        </li>
                        <li>✅ Add/remove levels without losing progress</li>
                        <li>✅ Changes apply from tomorrow onwards</li>
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <strong>Setting up vocabulary for the first time:</strong>{" "}
                    Choose when to start and how many words per day students
                    will learn.
                  </p>
                </div>
              )}

              {/* Level Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  German Levels *{" "}
                  <span className="text-red-500">
                    (Select at least one level)
                  </span>
                </label>
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  {["A1", "A2", "B1", "B2"].map((level) => (
                    <label
                      key={level}
                      className="flex items-center gap-3 cursor-pointer hover:bg-white p-2 rounded transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={vocabConfig.levels.includes(level)}
                        onChange={() => handleLevelToggle(level)}
                        className="w-5 h-5 text-[#009fe3] border-gray-300 rounded focus:ring-[#009fe3]"
                      />
                      <div className="flex-1">
                        <span className="font-semibold text-gray-800">
                          {level}
                        </span>
                        <span className="text-sm text-gray-600 ml-2">
                          {level === "A1" && "- Beginner (500 words)"}
                          {level === "A2" && "- Elementary (500 words)"}
                          {level === "B1" && "- Intermediate (1000 words)"}
                          {level === "B2" &&
                            "- Upper Intermediate (1000 words)"}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
                {vocabConfig.levels.length > 0 ? (
                  <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Selected: {vocabConfig.levels.join(", ")}
                  </p>
                ) : (
                  <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Please select at least one level to continue
                  </p>
                )}
              </div>

              {/* ✅ UPDATED: Start Date — now always editable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vocabulary Start Date *
                </label>
                <input
                  type="date"
                  required
                  value={vocabConfig.startDate}
                  onChange={(e) =>
                    setVocabConfig({
                      ...vocabConfig,
                      startDate: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {selectedBatch.vocabularyEnabled
                    ? "⚠️ Changing the start date will shift the day counter for all students in this batch."
                    : `Students will receive their first ${vocabConfig.wordsPerDay} words on this date`}
                </p>
              </div>

              {/* Words Per Day */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Words Per Day *
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  max="20"
                  value={vocabConfig.wordsPerDay}
                  onChange={(e) =>
                    setVocabConfig({
                      ...vocabConfig,
                      wordsPerDay: parseInt(e.target.value) || 5,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Recommended: 5 words per day (default)
                </p>
              </div>

              {/* Info Note */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> Students will receive words from all
                  selected levels. Words will be distributed evenly across
                  levels. After Day 100, the cycle restarts from Day 1
                  automatically.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowConfigModal(false);
                    setSelectedBatch(null);
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveVocabularyConfig}
                  disabled={vocabConfig.levels.length === 0}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {selectedBatch.vocabularyEnabled
                    ? "💾 Save Changes"
                    : "🚀 Enable Vocabulary"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchManagement;
